import {
  EuiAccordion,
  EuiBasicTable,
  EuiFlexGroup,
  EuiFormRow,
  EuiSpacer,
  EuiTableComputedColumnType,
  EuiText,
  EuiToken,
} from "@inscopix/ideas-eui";
import { DataTableColumnDefinition } from "pages/gdt/store/DataTableProvider.types";
import { COLUMN_KIND_MAP } from "../ModalCreateColumn/ModalCreateColumn";
import { ToolSpec } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { ReactNode } from "react";

export const convertParamTypeToColumnKind = (
  paramType: ToolSpec["params"][number]["type"]["param_type"],
): DataTableColumnDefinition["kind"] => {
  switch (paramType) {
    case "ToolBooleanParam":
      return "boolean";
    case "ToolCellStatusParam":
      return "cell_status";
    case "ToolChoiceParam":
      return "choice";
    case "ToolCroppingFrameParam":
      return "roi_frame";
    case "ToolFloatRangeParam":
      return "float";
    case "ToolIntRangeParam":
      return "integer";
    case "ToolPathParam":
      return "file";
    case "ToolRoiFrameParam":
      return "roi_frame";
    case "ToolStringParam":
      return "text";
  }
};

const columns: EuiTableComputedColumnType<{
  name: string;
  kind: DataTableColumnDefinition["kind"];
}>[] = [
  {
    name: "Name",
    render: ({ name }) => {
      return name;
    },
  },
  {
    name: "Type",
    width: "20%",
    render: ({ kind }) => {
      return (
        <EuiFlexGroup alignItems="center" gutterSize="s">
          <EuiToken iconType={COLUMN_KIND_MAP[kind].icon} />
          <span>{COLUMN_KIND_MAP[kind].name}</span>
        </EuiFlexGroup>
      );
    },
  },
];

type FieldParamTableProps = {
  autoFocus?: boolean;
  toolSpec: ToolSpec;
  resultsLabel?: string;
  inputsLabel?: string;
  isAlwaysExpanded?: boolean;
  hideEmptyParamsTable?: boolean;
  helpText?: ReactNode | ReactNode[];
  readOnly?: boolean;
  value: string[];
  onChange: (paramKeys: string[]) => void;
};

export const FieldParamTable = ({
  autoFocus = false,
  toolSpec,
  resultsLabel = "Analysis results",
  inputsLabel = "Analysis inputs & parameters",
  isAlwaysExpanded = false,
  hideEmptyParamsTable = false,
  helpText,
  readOnly = false,
  value,
  onChange,
}: FieldParamTableProps) => {
  let nonResultItems = [
    {
      id: "__id__",
      name: "ID",
      isIdColumn: true,
      isResult: false,
      kind: "identifier" as const,
    },
    ...toolSpec.params.map((param) => ({
      id: param.key,
      name: param.name,
      isIdColumn: false,
      isResult: false,
      kind: convertParamTypeToColumnKind(param.type.param_type),
    })),
  ];

  // Only show selected items when in read-only mode
  nonResultItems = readOnly
    ? nonResultItems.filter((item) => value.includes(item.id))
    : nonResultItems;

  let resultItems = toolSpec.results[0].files.map((result) => ({
    id: result.result_key,
    name: result.result_name,
    isIdColumn: false,
    isResult: true,
    kind: "file" as const,
  }));

  // Only show selected items when in read-only mode
  resultItems = readOnly
    ? resultItems.filter((item) => value.includes(item.id))
    : resultItems;

  return (
    <>
      <EuiFormRow fullWidth label={resultsLabel} helpText={helpText}>
        <EuiBasicTable
          autoFocus={autoFocus}
          columns={columns}
          items={resultItems}
          itemId={(item) => item.id}
          // Disable selection when in read-only mode
          selection={
            !readOnly
              ? {
                  selected: resultItems.filter(({ id }) => value.includes(id)),
                  onSelectionChange: (selected) => {
                    const resultIds = new Set(resultItems.map(({ id }) => id));
                    const newValue: string[] = [
                      ...value.filter((id) => !resultIds.has(id)),
                      ...selected.map(({ id }) => id),
                    ];
                    onChange(newValue);
                  },
                }
              : undefined
          }
        />
      </EuiFormRow>

      <EuiSpacer size="s" />

      {(!hideEmptyParamsTable || nonResultItems.length > 0) && (
        <EuiAccordion
          id="inputs_parameters"
          initialIsOpen={isAlwaysExpanded}
          isDisabled={isAlwaysExpanded}
          arrowDisplay={isAlwaysExpanded ? "none" : undefined}
          buttonContent={
            <EuiText
              color="rgb(26,28,33)"
              size="xs"
              style={{ cursor: "pointer" }}
            >
              <b>{inputsLabel}</b>
            </EuiText>
          }
        >
          <EuiBasicTable
            autoFocus={autoFocus}
            columns={columns}
            items={nonResultItems}
            itemId={(item) => item.id}
            // Disable selection when in read-only mode
            selection={
              !readOnly
                ? {
                    selected: nonResultItems.filter(({ id }) =>
                      value.includes(id),
                    ),
                    onSelectionChange: (selected) => {
                      const paramIds = new Set(
                        nonResultItems.map(({ id }) => id),
                      );
                      const newValue: string[] = [
                        ...value.filter((id) => !paramIds.has(id)),
                        ...selected.map(({ id }) => id),
                      ];
                      onChange(newValue);
                    },
                  }
                : undefined
            }
          />
        </EuiAccordion>
      )}
    </>
  );
};
